var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import _ from "lodash";
import { AbstractParseTreeVisitor } from "antlr4ts/tree/AbstractParseTreeVisitor";
import { FilterExpressionParser } from "../antlr/FilterExpressionParser.js";
import { VariantRange } from "./VariantRange.js";
import { VariantBoundary } from "./VariantBoundary.js";
import { getConjunction, getDisjunction } from "./VariantRange.utils.js";
class FilterRangesVisitor extends AbstractParseTreeVisitor {
  // defaultValue: default value to be returned from visitor methods.
  constructor(tryParseLiteral, compareValues, defaultValue) {
    super();
    __publicField(this, "_tryParseLiteral");
    __publicField(this, "_defaultValue");
    __publicField(this, "_compareValues");
    __publicField(this, "errors", []);
    __publicField(this, "LiteralValueComparer", (x, y) => {
      return VariantBoundary.compareValues(x, y, this._compareValues);
    });
    if (!tryParseLiteral) {
      throw new Error("Literal parser is null");
    }
    this._tryParseLiteral = tryParseLiteral;
    this._defaultValue = defaultValue;
    this._compareValues = compareValues;
  }
  defaultResult() {
    return this._defaultValue;
  }
  visitFilterConjunction(context) {
    const ranges = [];
    const lhsRanges = context.filterExpr(0).accept(this);
    _.each(context.filterExpr(1).accept(this), (rhsRange) => {
      _.each(lhsRanges, (lhsRange) => {
        const overlap = getConjunction(lhsRange, rhsRange, this._compareValues);
        if (overlap !== null) {
          ranges.push(overlap);
        }
      });
    });
    return ranges;
  }
  visitFilterDisjunction(context) {
    const ranges = _.flatMap(context.filterExpr(), (filterExpr) => filterExpr.accept(this));
    let updated;
    do {
      updated = false;
      for (let i = 0; i < ranges.length; i++) {
        if (ranges[i] === null) {
          continue;
        }
        for (let j = i + 1; j < ranges.length; j++) {
          if (ranges[j] === null) {
            continue;
          }
          const merged = getDisjunction(ranges[i], ranges[j], this._compareValues);
          if (merged != null) {
            ranges[i] = merged;
            ranges[j] = null;
            updated = true;
          }
        }
      }
    } while (updated);
    return _.filter(ranges, (range) => range !== null && range !== void 0);
  }
  visitFilterGroup(context) {
    return context.filterExpr().accept(this);
  }
  visitFilterBinary(context) {
    let op = context._op;
    let literal = context.literal();
    const { value: literalValue, success: tryParseResult } = this._tryParseLiteral(literal);
    if (!tryParseResult) {
      this.addError(`Expected a <T> literal instead of "${literal.text}"`);
      return [];
    }
    if (op.type === FilterExpressionParser.NE) {
      return [
        VariantRange.upperBound(literalValue, false, this._compareValues),
        VariantRange.lowerBound(literalValue, false, this._compareValues)
      ];
    } else {
      switch (op.type) {
        case FilterExpressionParser.EQ:
          return [VariantRange.equalTo(literalValue, this._compareValues)];
        case FilterExpressionParser.LE:
          return [FilterRangesVisitor.lessThanOrEqual(literalValue, this._compareValues)];
        case FilterExpressionParser.LT:
          return [FilterRangesVisitor.lessThan(literalValue, this._compareValues)];
        case FilterExpressionParser.GE:
          return [FilterRangesVisitor.greaterThanOrEqual(literalValue, this._compareValues)];
        case FilterExpressionParser.GT:
          return [FilterRangesVisitor.greaterThan(literalValue, this._compareValues)];
        default:
          throw new Error(`Unknown operator "${op}" in expression "${context}".`);
      }
    }
  }
  visitFilterIn(context) {
    const op = context._op;
    const literals = [];
    _.each(context.literalList().literal(), (literal) => {
      const { value: literalValue, success: tryParseResult } = this._tryParseLiteral(literal);
      if (tryParseResult) {
        literals.push(literalValue);
      } else {
        this.addError(`Expected a <T> literal instead of "${literal.text}"`);
      }
    });
    const sortedLiterals = Array.from(new Set(literals)).sort(this.LiteralValueComparer);
    if (sortedLiterals.length === 0) {
      return [];
    }
    if (op.type === FilterExpressionParser.IN) {
      return _.map(sortedLiterals, (literal) => {
        return VariantRange.equalTo(literal, this._compareValues);
      });
    } else if (op.type === FilterExpressionParser.NOTIN || op.type === FilterExpressionParser.NE) {
      const result = [];
      let previous = sortedLiterals[0];
      result.push(FilterRangesVisitor.lessThan(previous, this._compareValues));
      _.each(sortedLiterals.slice(1), (current) => {
        result.push(VariantRange.between(current, false, previous, false, this._compareValues));
        previous = current;
      });
      result.push(FilterRangesVisitor.greaterThan(previous, this._compareValues));
      return result;
    } else {
      throw new Error(`Unknown operator "${op}" in expression "${context}".`);
    }
  }
  visitFilterBetween(context) {
    const lowerBoundToken = context.literal(0);
    const upperBoundToken = context.literal(1);
    const { value: lowerBound, success: tryParseLowerBound } = this._tryParseLiteral(lowerBoundToken);
    const { value: upperBound, success: tryParseUpperBound } = this._tryParseLiteral(upperBoundToken);
    if (tryParseLowerBound && tryParseUpperBound) {
      return [FilterRangesVisitor.between(lowerBound, upperBound, this._compareValues)];
    } else {
      this.addError(`Expected two <T> literals in "BETWEEN ${lowerBoundToken.text} AND ${upperBoundToken.text}".`);
    }
    return [];
  }
  addError(message) {
    this.errors.push(new Error(message));
  }
  static lessThan(lessThan, compareValues) {
    return VariantRange.upperBound(lessThan, false, compareValues);
  }
  static lessThanOrEqual(lessThan, compareValues) {
    return VariantRange.upperBound(lessThan, true, compareValues);
  }
  static greaterThan(greaterThan, compareValues) {
    return VariantRange.lowerBound(greaterThan, false, compareValues);
  }
  static greaterThanOrEqual(greaterThan, compareValues) {
    return VariantRange.lowerBound(greaterThan, true, compareValues);
  }
  static between(lowerBound, upperBound, compareValues) {
    return VariantRange.between(upperBound, true, lowerBound, true, compareValues);
  }
}
export {
  FilterRangesVisitor
};
//# sourceMappingURL=FilterRangesVisitor.js.map
